import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Plus, Trash2, X, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { downscaleImage } from "@/lib/eventLib";
import { SAMPLE_PHOTO, resolveText, resolveImage } from "@/components/events/PassTemplateView";

const defaultTemplate = () => ({ enabled: true, image: "", width: 9, height: 13, unit: "cm", orientation: "portrait", elements: [] });
const eid = () => "e_" + Math.random().toString(36).slice(2, 8);

const mkEl = (p) => ({
  id: eid(), kind: p.kind, bind: p.bind, label: p.label,
  x: 50, y: 50,
  size: p.kind === "text" ? 6 : p.kind === "qr" ? 20 : 24,
  color: "#111111", weight: "700", align: "center",
});

export default function PassTemplateEditor({ template, onChange, fields, eventName, serialPrefix }) {
  const t = template && Object.keys(template).length ? template : { enabled: false };
  const [sel, setSel] = useState(null);
  const [cw, setCw] = useState(0);
  const [sampleQr, setSampleQr] = useState("");
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => { QRCode.toDataURL("SAMPLE-PASS", { width: 300, margin: 1 }).then(setSampleQr).catch(() => {}); }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ro = new ResizeObserver((ents) => setCw(ents[0].contentRect.width));
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [t.enabled, t.image]);

  const patch = (p) => onChange({ ...defaultTemplate(), ...t, ...p });
  const updEl = (id, p) => onChange({ ...t, elements: (t.elements || []).map((e) => (e.id === id ? { ...e, ...p } : e)) });
  const addEl = (pal) => { const n = (t.elements || []).length; const el = { ...mkEl(pal), x: 50, y: Math.min(88, 18 + (n % 9) * 8) }; onChange({ ...defaultTemplate(), ...t, elements: [...(t.elements || []), el] }); setSel(el.id); };
  const delEl = (id) => { onChange({ ...t, elements: (t.elements || []).filter((e) => e.id !== id) }); setSel(null); };

  const onUpload = async (file) => {
    if (!file) return;
    try { patch({ image: await downscaleImage(file, 1600, 0.85) }); }
    catch { toast.error("Template image load fail hua"); }
  };

  const setOrientation = (o) => {
    let w = Number(t.width) || 9, h = Number(t.height) || 13;
    if (o === "landscape" && h > w) [w, h] = [h, w];
    if (o === "portrait" && w > h) [w, h] = [h, w];
    patch({ orientation: o, width: w, height: h });
  };

  // drag — element canvas ke andar hi rahe, edge se kate nahi
  const onDown = (e, id) => {
    e.stopPropagation(); e.preventDefault();
    setSel(id); dragRef.current = id;
    const box = e.currentTarget.getBoundingClientRect();
    const hw = box.width / 2, hh = box.height / 2;
    const move = (ev) => {
      if (!dragRef.current || !canvasRef.current) return;
      const r = canvasRef.current.getBoundingClientRect();
      let px = ev.clientX - r.left, py = ev.clientY - r.top;
      px = Math.min(r.width - hw, Math.max(hw, px));
      py = Math.min(r.height - hh, Math.max(hh, py));
      updEl(dragRef.current, { x: Math.round((px / r.width) * 1000) / 10, y: Math.round((py / r.height) * 1000) / 10 });
    };
    const up = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  // kisi position par element ki max allowed size (taaki canvas se bahar na kate)
  const maxSizeAtPos = (el) => {
    const c = canvasRef.current;
    if (!c) return el.kind === "text" ? 18 : 100;
    const r = c.getBoundingClientRect();
    const cx = (el.x / 100) * r.width, cy = (el.y / 100) * r.height;
    if (el.kind === "text") return Math.min(18, (Math.min(cy, r.height - cy) / 0.55 / r.width) * 100);
    return Math.min(100, (2 * Math.min(cx, r.width - cx, cy, r.height - cy) / r.width) * 100);
  };

  // anchored resize: QR/image ka top-left corner fix rehta hai, box sirf badhta/ghat-ta hai —
  // isse resize karte time element idhar-udhar nahi kudta aur canvas se bahar nahi jata
  const resizeAnchored = (el, ns) => {
    const c = canvasRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const oldPx = (el.size / 100) * r.width;
    const leftPx = (el.x / 100) * r.width - oldPx / 2;
    const topPx = (el.y / 100) * r.height - oldPx / 2;
    const maxSp = Math.min(100, Math.min((r.width - leftPx) / r.width, (r.height - topPx) / r.height) * 100);
    const sp = Math.max(2, Math.min(ns, maxSp));
    const nPx = (sp / 100) * r.width;
    updEl(el.id, { size: Math.round(sp * 10) / 10, x: Math.round(((leftPx + nPx / 2) / r.width) * 1000) / 10, y: Math.round(((topPx + nPx / 2) / r.height) * 1000) / 10 });
  };

  // resize via corner handle
  const onResize = (e, el) => {
    e.stopPropagation(); e.preventDefault();
    setSel(el.id);
    const r = canvasRef.current.getBoundingClientRect();
    if (el.kind === "text") {
      const cy = r.top + (el.y / 100) * r.height;
      const move = (ev) => {
        const d = Math.abs(ev.clientY - cy);
        let sp = (d * 2 / r.width) * 100;
        sp = Math.min(18, maxSizeAtPos(el), Math.max(2, Math.round(sp * 10) / 10));
        updEl(el.id, { size: sp });
      };
      const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
      window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
      return;
    }
    const sizePx = (el.size / 100) * r.width;
    const leftPx = r.left + (el.x / 100) * r.width - sizePx / 2;
    const topPx = r.top + (el.y / 100) * r.height - sizePx / 2;
    const move = (ev) => {
      const d = Math.max(ev.clientX - leftPx, ev.clientY - topPx);
      resizeAnchored(el, (d / r.width) * 100);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const palette = [
    ...(fields || []).map((f) => ({ kind: f.type === "photo" ? "image" : "text", bind: f.key, label: f.label || "Field" })),
    { kind: "text", bind: "__name", label: "Candidate Name" },
    { kind: "text", bind: "__serial", label: "Serial No" },
    { kind: "text", bind: "__event", label: "Event Name" },
    { kind: "qr", bind: "__qr", label: "QR Code" },
  ];

  const ctx = {
    data: Object.fromEntries((fields || []).map((f) => [f.key, f.type === "photo" ? SAMPLE_PHOTO : (f.label || "Value")])),
    serial_no: 1, candidate_name: "Candidate Name", event_name: eventName || "Event", qrDataUrl: sampleQr, serial_prefix: serialPrefix || "",
  };

  const w = Number(t.width) || 9, h = Number(t.height) || 13;
  const ch = cw * (h / w);
  const selEl = (t.elements || []).find((e) => e.id === sel);

  return (
    <section className="bg-card border border-border rounded-xl p-4 space-y-3">
      <label className="flex items-center gap-2 font-heading font-semibold text-sm cursor-pointer">
        <input type="checkbox" checked={!!t.enabled} onChange={(e) => (e.target.checked ? onChange(defaultTemplate()) : onChange({ ...t, enabled: false }))} data-testid="tmpl-toggle" />
        <LayoutTemplate className="h-4 w-4" /> Custom Entry Pass Design (apna template upload karein)
      </label>

      {t.enabled && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Apna banaya hua pass design upload karein, phir data fields aur QR ko drag karke set karein. Candidate ko yahi design dikhega.</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="space-y-1"><Label className="text-xs">Unit</Label>
              <Select value={t.unit || "cm"} onValueChange={(v) => patch({ unit: v })}>
                <SelectTrigger data-testid="tmpl-unit" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="mm">mm</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Width</Label><Input type="number" value={t.width} onChange={(e) => patch({ width: Number(e.target.value) })} data-testid="tmpl-width" className="h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Height</Label><Input type="number" value={t.height} onChange={(e) => patch({ height: Number(e.target.value) })} data-testid="tmpl-height" className="h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Orientation</Label>
              <Select value={t.orientation || "portrait"} onValueChange={setOrientation}>
                <SelectTrigger data-testid="tmpl-orientation" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="portrait">Portrait</SelectItem><SelectItem value="landscape">Landscape</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="file" accept="image/*" hidden ref={imgRef} data-testid="tmpl-upload-input" onChange={(e) => { onUpload(e.target.files[0]); e.target.value = ""; }} />
            <Button variant="outline" onClick={() => imgRef.current?.click()} data-testid="tmpl-upload-btn" className="gap-1.5 h-9"><Upload className="h-4 w-4" /> {t.image ? "Change Template" : "Upload Template"}</Button>
            {t.image && <button onClick={() => patch({ image: "" })} className="text-red-500 text-xs flex items-center gap-1" data-testid="tmpl-remove-img"><X className="h-3.5 w-3.5" /> Remove</button>}
          </div>

          {t.image ? (
            <>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs text-muted-foreground w-full">Element add karein, phir drag karke move aur corner (nila dot) se size badlein:</span>
                {palette.map((p) => (
                  <button key={p.bind} onClick={() => addEl(p)} data-testid={`tmpl-add-${p.bind}`} className="text-xs border border-border rounded-full px-2.5 py-1 hover:bg-cyan-50 hover:border-cyan-300 flex items-center gap-1">
                    <Plus className="h-3 w-3" /> {p.label}
                  </button>
                ))}
              </div>

              <div className="mx-auto" style={{ maxWidth: w >= h ? 560 : 360, width: "100%" }}>
                <div
                  ref={canvasRef}
                  onPointerDown={(e) => { if (e.target === canvasRef.current) setSel(null); }}
                  className="relative w-full border rounded-lg overflow-hidden bg-slate-100 select-none touch-none"
                  style={{ aspectRatio: `${w} / ${h}` }}
                  data-testid="tmpl-canvas"
                >
                  <img src={t.image} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                  {cw > 0 && (t.elements || []).map((el) => {
                    const left = (el.x / 100) * cw, top = (el.y / 100) * ch;
                    const isSel = el.id === sel;
                    const isImg = el.kind === "qr" || el.kind === "image";
                    const sizePx = (el.size / 100) * cw;
                    return (
                      <div key={el.id} onPointerDown={(e) => onDown(e, el.id)} onClick={(e) => e.stopPropagation()}
                        style={{ position: "absolute", left, top, transform: "translate(-50%,-50%)", cursor: "move", touchAction: "none", outline: isSel ? "2px solid #06b6d4" : "1px dashed rgba(0,0,0,.3)" }}>
                        {isImg ? (
                          <img src={resolveImage(el, ctx)} alt="" draggable={false} style={{ display: "block", width: sizePx, height: sizePx, objectFit: "cover", pointerEvents: "none" }} />
                        ) : (
                          <div style={{ fontSize: sizePx, color: el.color, fontWeight: el.weight, whiteSpace: "nowrap", lineHeight: 1.1, padding: "1px 3px", pointerEvents: "none" }}>{resolveText(el, ctx)}</div>
                        )}
                        {isSel && <span onPointerDown={(e) => onResize(e, el)} data-testid="tmpl-resize" title="Drag to resize" style={{ position: "absolute", right: -8, bottom: -8, width: 18, height: 18, borderRadius: 5, background: "#06b6d4", border: "2px solid #fff", boxShadow: "0 1px 3px rgba(0,0,0,.3)", cursor: "nwse-resize", touchAction: "none" }} />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {selEl ? (
                <div className="border border-border rounded-lg p-3 space-y-2.5 bg-slate-50" data-testid="tmpl-controls">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{selEl.label}</span>
                    <button onClick={() => delEl(selEl.id)} className="text-red-500" data-testid="tmpl-del-el"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs w-20">{selEl.kind === "text" ? "Font size" : "Size"}</Label>
                    <input type="range" min={selEl.kind === "text" ? 2 : 2} max={selEl.kind === "text" ? 18 : 100} step="0.5" value={selEl.size} onChange={(e) => (selEl.kind === "text" ? updEl(selEl.id, { size: Math.min(Number(e.target.value), maxSizeAtPos(selEl)) }) : resizeAnchored(selEl, Number(e.target.value)))} className="flex-1" data-testid="tmpl-size" />
                  </div>
                  {selEl.kind === "text" && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="flex items-center gap-1.5 text-xs"><span>Colour</span><input type="color" value={selEl.color} onChange={(e) => updEl(selEl.id, { color: e.target.value })} data-testid="tmpl-color" className="h-7 w-9 border rounded" /></label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={selEl.weight === "700"} onChange={(e) => updEl(selEl.id, { weight: e.target.checked ? "700" : "400" })} data-testid="tmpl-bold" /> Bold</label>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center">Kisi element ko tap/click karein — phir corner (nila dot) se size, ya niche slider/colour se look change karein.</p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-6 text-center">Pehle apna pass template (design image) upload karein.</p>
          )}
        </div>
      )}
    </section>
  );
}
